import type * as React from "react";

import { Button } from "./Button";
import { TriangleExclamationIcon } from "./Icons";
import { cn } from "../lib/cn";

export interface PanelErrorProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /**
   * What didn't load, as the tail of "I couldn't load ___." - lowercase and
   * carrying its own article: "the timeline", "your memos".
   */
  label: string;
  /** Ask again. Normally the error boundary's reset. */
  onRetry: () => void;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The failure state a panel drops into when its fetch gives up: what didn't load,
 * and one way to ask again. Written as a quiet line rather than an alarm, with the
 * stopped ink carrying the signal, since a panel that failed is a note in the
 * margin of the page, not the page.
 *
 * Body ink, not annotation: this renders on paper inside panels but also straight
 * onto the workspace corkboard, and annotation warm on cork tan is too close to
 * read.
 *
 * It fills its container (`h-full`), so give it the panel's whole body rather
 * than a slot inside one.
 */
export function PanelError({
  label,
  onRetry,
  className,
  ...rest
}: PanelErrorProps) {
  return (
    <div
      className={cn(
        "flex h-full min-w-0 flex-col items-center justify-center gap-3 p-10 text-center font-body",
        className,
      )}
      {...rest}
    >
      <p className="flex min-w-0 items-center gap-2 text-sm text-ink-body">
        {/* No space between glyph and words: the flex `gap-2` is the space. */}
        <TriangleExclamationIcon className="text-ink-stopped" />I couldn&rsquo;t
        load {label}.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
